import React, { useState } from 'react';
import { ReactSVG } from 'react-svg';

import styled from '@emotion/styled';

import {
  BREAKPOINTS,
  disabledColor,
  primaryColor,
  unit,
  unitTRBL,
} from '@zego/theme';
import { useTracking } from '@zego/tracking';

import H3 from '@zego/components/atoms/H3';
import Spacer from '@zego/components/atoms/Spacer';
import View from '@zego/components/atoms/View';
import ComparisonTableSection from '@zego/components/molecules/ComparisonTableSection';

import H2 from '../atoms/H2';
import ContentContainer from '../ContentContainer';
import DuoToggleButton, {
  DuoToggleButtonArrayProps,
} from '../molecules/DuoToggleButton';

import FixedIcon from '@zego/static/svgs/FixedIcon.svg';
import UsageRateIcon from '@zego/static/svgs/UsageRateIcon.svg';

const Title = styled(H2)({
  textAlign: 'center',
  fontSize: 42,
  paddingTop: unit(8),

  [BREAKPOINTS.smallOnly]: {
    fontSize: 32,
  },
});

const SubTitle = styled(H3)({
  color: disabledColor(8),
  fontSize: 18,
  textAlign: 'center',
  fontWeight: 400,
  lineHeight: '26px',
  [BREAKPOINTS.small]: {
    fontSize: 16,
    lineHeight: '22px',
  },
});

const FleetDuoToggleButtonsStyling = styled(View)({
  display: 'flex',
  justifyContent: 'center',
  margin: 'auto',
  paddingBottom: unit(3),

  width: unit(40),
  [BREAKPOINTS.smallOnly]: {
    width: unit(31),
  },
  [BREAKPOINTS.medium]: {
    width: unit(52),
  },
});

const ButtonTextStyling = styled.span({
  fontSize: 16,
  [BREAKPOINTS.smallOnly]: { paddingTop: 2 },
});

const SmallTextStyling = styled(H3)({
  color: disabledColor(8),
  fontSize: 14,
  fontWeight: 400,
  textAlign: 'left',
  paddingLeft: unit(2),
  paddingTop: unit(4),
  [BREAKPOINTS.small]: {
    textAlign: 'center',
    fontSize: 16,
    lineHeight: '22px',
  },
  [BREAKPOINTS.medium]: { textAlign: 'left' },
});

const StyledSVGIcon = styled(ReactSVG)<StyledSVGIconProps>`
      svg {
        path {
          stroke: ${(p): string => p.color}
        }
      }
    `;

interface StyledSVGIconProps {
  color: string;
}

interface TableHeaderProps {
  label: string;
  icon: string;
}

interface RowColumnValueProps {
  checked: boolean;
}

interface RowProps {
  description: string;
  columns: RowColumnValueProps[];
}

interface ComparisonTableCopy {
  tableHeaders: Array<TableHeaderProps>;
  rows: Array<RowProps>;
  showHeaderIcons?: boolean;
  listPadding?: string;
}

export interface ComparisonTableCopyProps {
  firstTable: ComparisonTableCopy;
  secondTable: ComparisonTableCopy;
  title: string;
  subTitle: string;
  footerText: string;
}

export default function WhichPolicySection({
  firstTable,
  secondTable,
  title,
  subTitle,
  footerText,
}: ComparisonTableCopyProps): JSX.Element {
  const coverOptionOne = 'Usage based';
  const coverOptionTwo = 'Fixed';
  const iconOne = UsageRateIcon;
  const iconTwo = FixedIcon;
  const [selectedCoverOption, setSelectedCoverOption] = useState<string>(
    coverOptionOne,
  );

  const { trackEvent } = useTracking();

  const handleChange = (option: string): void => {
    setSelectedCoverOption(option);
    trackEvent(`${option} toggle - Cover table`);
  };
  const WhichPolicyDetails =
    selectedCoverOption === coverOptionOne ? (
      <ComparisonTableSection
        showHeaderIcons={false}
        listPadding="0px"
        {...firstTable}
      />
    ) : (
      <ComparisonTableSection
        showHeaderIcons={false}
        listPadding="0px"
        {...secondTable}
      />
    );

  const PolicyOptionsArray: Array<DuoToggleButtonArrayProps> = [
    {
      heading: coverOptionOne,
      node: (
        <>
          <StyledSVGIcon
            src={iconOne}
            color={
              selectedCoverOption === coverOptionOne
                ? 'white'
                : primaryColor(10)
            }
            css={{
              display: 'flex',
              justifyContent: 'center',
              paddingRight: 10,
            }}
          />
          <ButtonTextStyling>{coverOptionOne}</ButtonTextStyling>
        </>
      ),
    },
    {
      heading: coverOptionTwo,
      node: (
        <>
          <StyledSVGIcon
            src={iconTwo}
            color={
              selectedCoverOption === coverOptionTwo
                ? 'white'
                : primaryColor(10)
            }
            css={{
              display: 'flex',
              justifyContent: 'center',
              paddingRight: 10,
            }}
          />
          <ButtonTextStyling>{coverOptionTwo}</ButtonTextStyling>
        </>
      ),
    },
  ];
  return (
    <View
      css={{
        backgroundColor: primaryColor(1),
        padding: unitTRBL(0, 1, 4, 1),
        [BREAKPOINTS.large]: {
          padding: unitTRBL(4, 12, 12, 12),
        },
      }}
    >
      <ContentContainer>
        <Title>{title}</Title>
        <Spacer height={unit(4)} />

        <SubTitle>{subTitle}</SubTitle>
        <Spacer height={unit(4)} />

        <FleetDuoToggleButtonsStyling>
          <DuoToggleButton
            selectedOption={selectedCoverOption}
            onChange={handleChange}
          >
            {PolicyOptionsArray}
          </DuoToggleButton>
        </FleetDuoToggleButtonsStyling>
        {WhichPolicyDetails}
        <SmallTextStyling>{footerText}</SmallTextStyling>
      </ContentContainer>
    </View>
  );
}
